"use client";

import { formatCentsWithCurrency } from "@/lib/formatting/currency";
import { cn } from "@/lib/utils";
import { useGradeSelection, type GradePrice } from "./grade-selection";

function LadderRow({
  grade,
  highest,
  isSelected,
  onToggle,
  currency,
}: {
  grade: GradePrice;
  highest: number;
  isSelected: boolean;
  onToggle: () => void;
  currency: "USD" | "CAD";
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={isSelected}
        onClick={onToggle}
        className="grid w-full grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="truncate">{grade.label}</span>
        <span className="h-2 rounded-full bg-border-subtle">
          <span
            className={cn(
              "block h-full rounded-full",
              isSelected ? "bg-action" : "bg-fg-muted/40",
            )}
            style={{ width: `${(grade.avgPriceCents / highest) * 100}%` }}
          />
        </span>
        <span className="tabular-nums">
          {formatCentsWithCurrency(grade.avgPriceCents, currency)}
        </span>
      </button>
    </li>
  );
}

export function GradeLadder({
  currency,
  scopeName,
}: {
  currency: "USD" | "CAD";
  scopeName: string | null;
}) {
  const { grades, selected, select } = useGradeSelection();
  if (grades.length === 0) return null;
  const ranked = grades.slice().sort((a, b) => b.avgPriceCents - a.avgPriceCents);
  const highest = ranked[0]?.avgPriceCents ?? 1;
  return (
    <section className="grid gap-3">
      <h2 className="text-base font-semibold text-foreground">
        By grade{scopeName ? ` in ${scopeName}` : ""}
      </h2>
      <ul className="grid gap-1">
        {ranked.map((grade) => {
          const isSelected = selected?.key === grade.key;
          return (
            <LadderRow
              key={grade.key}
              grade={grade}
              highest={highest}
              isSelected={isSelected}
              onToggle={() => select(isSelected ? null : grade.key)}
              currency={currency}
            />
          );
        })}
      </ul>
    </section>
  );
}
